import type { Rect, UiRects } from "../../game/input";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../../game/constants";
import { mobileFontSize } from "../../game/platform";
import type { RenderModel } from "../renderModel";
import type { drawButton } from "../ui";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  menuBtnH,
  menuButtonColumns,
  menuGap,
  mFont,
  mFontPlain,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface TokenShopDeps {
  ctx: CanvasRenderingContext2D;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawTokenShopScreen(deps: TokenShopDeps, model: RenderModel): UiRects {
  const { ctx, drawBtn, drawCircleCloseButton, drawGemIcon, drawHourglass } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);
  const uiRects: UiRects = {};
  const g = menuGap();
  const titleY = 132;
  const packW = menuCols.fullW;
  const packH = menuBtnH();
  const gapY = menuGap();
  const startX = menuCols.fullX;

  // Title
  ctx.fillStyle = "#14f195";
  ctx.font = `bold 11px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const headerLabel = model.activeTokenSymbol
    ? `PAY WITH $${model.activeTokenSymbol}`
    : "PAY WITH SOL";
  ctx.fillText(headerLabel, centerX, titleY);

  // SOL balance row (if wallet connected)
  const balanceY = titleY + mobileFontSize(11) + g;
  if (model.solWalletConnected) {
    const solBal = model.solTokenBalances.find(b => b.symbol === "SOL");
    if (solBal) {
      ctx.fillStyle = "#a0f0d0";
      ctx.font = mFontPlain(6);
      const usdStr = solBal.usdValue != null ? ` ($${solBal.usdValue.toFixed(2)})` : "";
      ctx.fillText(`Balance: ${solBal.balance.toFixed(4)} SOL${usdStr}`, centerX, balanceY);
    }
  }

  const itemStartY = balanceY + mobileFontSize(6) + g;

  if (!model.solWalletConnected) {
    // Wallet not connected — connect button
    const connectRect: Rect = { x: menuCols.fullX, y: itemStartY, w: menuCols.fullW, h: menuBtnH() };
    drawBtn(connectRect, "CONNECT WALLET", {
      fill: "rgba(5,30,20,0.92)",
      stroke: "#14f195",
      font: mFont("bold", 7),
      text: "#14f195",
    });
    uiRects.solWalletConnect = connectRect;

    const backRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
    drawCircleCloseButton(backRect);
    uiRects.tokenShopBack = backRect;
  } else {
    // Token shop items
    const tokenItemRects: Rect[] = [];

    for (let i = 0; i < model.tokenShopItems.length; i++) {
      const item = model.tokenShopItems[i];
      const py = itemStartY + i * (packH + gapY);

      // Item card with Solana green accent
      roundedRectPath(ctx, startX, py, packW, packH, 6);
      ctx.fillStyle = "rgba(5,30,20,0.92)";
      ctx.fill();
      roundedRectPath(ctx, startX, py, packW, packH, 6);
      ctx.strokeStyle = "#14f195";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Left: gem icon + item name
      drawGemIcon(startX + 8, py + 8, 14);
      ctx.fillStyle = "#14f195";
      ctx.font = mFont("bold", 7);
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(item.displayName, startX + 28, py + packH / 2);

      // Right: token price with symbol
      const priceStr = item.tokenPrice != null
        ? `${item.tokenPrice.toFixed(4)}${item.tokenSymbol ? ` $${item.tokenSymbol}` : ""}`
        : "...";
      ctx.fillStyle = "#a0f0d0";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "right";
      ctx.fillText(priceStr, startX + packW - 8, py + packH / 2);

      ctx.textAlign = "center";

      tokenItemRects.push({ x: startX, y: py, w: packW, h: packH });
    }
    uiRects.tokenShopItem = tokenItemRects;

    // Back button
    const backRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
    drawCircleCloseButton(backRect);
    uiRects.tokenShopBack = backRect;
  }

  // --- Token Purchase Flow Overlays ---
  const tps = model.tokenPurchaseState;

  if (tps === "confirming") {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const dialogW = packW - 16;
    const dialogH = 130;
    const dialogX = centerX - dialogW / 2;
    const dialogY = CANVAS_HEIGHT / 2 - dialogH / 2;

    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 10);
    ctx.fillStyle = "rgba(5,15,25,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 10);
    ctx.strokeStyle = "#14f195";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#14f195";
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("CONFIRM PURCHASE", centerX, dialogY + 12);

    if (model.tokenSelectedItemName != null) {
      ctx.fillStyle = MENU_TEXT_PRIMARY;
      ctx.font = mFont("bold", 7);
      ctx.fillText(model.tokenSelectedItemName, centerX, dialogY + 32);
    }

    const amountStr = model.tokenSelectedAmount ?? "Calculating...";
    ctx.fillStyle = "#a0f0d0";
    ctx.font = mFont("bold", 8);
    ctx.fillText(amountStr, centerX, dialogY + 52);

    const btnW = (dialogW - 24) / 2;
    const btnH = 30;
    const btnY = dialogY + dialogH - btnH - 12;

    const cancelRect: Rect = { x: dialogX + 8, y: btnY, w: btnW, h: btnH };
    drawBtn(cancelRect, "CANCEL", { fill: "#1a0a0a", stroke: "#ff6b6b", font: mFont("bold", 6) });
    uiRects.tokenShopCancel = cancelRect;

    const confirmRect: Rect = { x: dialogX + 8 + btnW + 8, y: btnY, w: btnW, h: btnH };
    drawBtn(confirmRect, "CONFIRM", { fill: "#0a1a10", stroke: "#14f195", font: mFont("bold", 6) });
    uiRects.tokenShopConfirm = confirmRect;
  }

  if (tps === "signing") {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const loadW = 180;
    const loadH = 80;
    const loadX = centerX - loadW / 2;
    const loadY = CANVAS_HEIGHT / 2 - loadH / 2;

    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.fillStyle = "rgba(5,15,25,0.98)";
    ctx.fill();
    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.strokeStyle = "#14f195";
    ctx.lineWidth = 2;
    ctx.stroke();

    drawHourglass(centerX, loadY + 30, 24, "#14f195");

    ctx.fillStyle = "#a0f0d0";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("APPROVE IN WALLET...", centerX, loadY + 52);
  }

  if (tps === "verifying") {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const attemptCount = model.tokenVerifyAttemptCount ?? 0;
    const attemptStr = attemptCount > 0 ? ` (attempt ${attemptCount}/5)` : "";

    const loadW = 200;
    const loadH = attemptCount >= 3 ? 120 : 80;
    const loadX = centerX - loadW / 2;
    const loadY = CANVAS_HEIGHT / 2 - loadH / 2;

    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.fillStyle = "rgba(5,15,25,0.98)";
    ctx.fill();
    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.strokeStyle = "#14f195";
    ctx.lineWidth = 2;
    ctx.stroke();

    drawHourglass(centerX, loadY + 30, 24, "#14f195");

    ctx.fillStyle = "#a0f0d0";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`CONFIRMING ON-CHAIN...${attemptStr}`, centerX, loadY + 52);

    if (attemptCount >= 3) {
      const recheckW = 100;
      const recheckH = 28;
      const recheckRect: Rect = { x: centerX - recheckW / 2, y: loadY + 72, w: recheckW, h: recheckH };
      drawBtn(recheckRect, "RECHECK", { fill: "#0a2015", stroke: "#14f195", font: mFont("bold", 6) });
      uiRects.tokenShopRecheck = recheckRect;
    }
  }

  if (tps === "success") {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const dlgW = packW - 16;
    const dlgH = 80;
    const dlgX = centerX - dlgW / 2;
    const dlgY = CANVAS_HEIGHT / 2 - dlgH / 2;

    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.fillStyle = "rgba(5,30,15,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.strokeStyle = "#14f195";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#14f195";
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("PURCHASE COMPLETE!", centerX, dlgY + 16);

    ctx.fillStyle = "#a0f0d0";
    ctx.font = mFontPlain(5);
    ctx.fillText("Tap anywhere to dismiss", centerX, dlgY + 38);

    uiRects.tokenShopDismiss = { x: 0, y: 0, w: CANVAS_WIDTH, h: CANVAS_HEIGHT };
  }

  if (tps === "processing") {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const dlgW = packW - 16;
    const dlgH = 80;
    const dlgX = centerX - dlgW / 2;
    const dlgY = CANVAS_HEIGHT / 2 - dlgH / 2;

    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.fillStyle = "rgba(20,20,5,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.strokeStyle = "#ffd700";
    ctx.lineWidth = 2;
    ctx.stroke();

    drawHourglass(centerX, dlgY + 28, 24, "#ffd700");

    ctx.fillStyle = "#ffd700";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("TRANSACTION PROCESSING...", centerX, dlgY + 48);

    ctx.fillStyle = "#aaa";
    ctx.font = mFontPlain(5);
    ctx.fillText("Tap anywhere to dismiss", centerX, dlgY + 64);

    uiRects.tokenShopDismiss = { x: 0, y: 0, w: CANVAS_WIDTH, h: CANVAS_HEIGHT };
  }

  if (tps === "error" && model.tokenPurchaseError != null) {
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const dlgW = packW - 16;
    const dlgH = 100;
    const dlgX = centerX - dlgW / 2;
    const dlgY = CANVAS_HEIGHT / 2 - dlgH / 2;

    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.fillStyle = "rgba(40,10,10,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dlgX, dlgY, dlgW, dlgH, 10);
    ctx.strokeStyle = "#ff6b6b";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#ff6b6b";
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("PURCHASE FAILED", centerX, dlgY + 14);

    ctx.fillStyle = "#ccc";
    ctx.font = mFontPlain(5);
    const errorText = model.tokenPurchaseError.length > 40
      ? model.tokenPurchaseError.slice(0, 40) + "..."
      : model.tokenPurchaseError;
    ctx.fillText(errorText, centerX, dlgY + 36);

    const dismissBtnW = (dlgW - 24) / 2;
    const dismissBtnH = 30;
    const dismissBtnY = dlgY + dlgH - dismissBtnH - 10;
    const dismissRect: Rect = { x: centerX - dismissBtnW / 2, y: dismissBtnY, w: dismissBtnW, h: dismissBtnH };
    drawBtn(dismissRect, "DISMISS", { fill: "#2a0a0a", stroke: "#ff6b6b", font: mFont("bold", 6) });
    uiRects.tokenShopDismiss = dismissRect;
  }

  return uiRects;
}
